import {
  type BackNForthSectionProps,
  type CarouselItems,
  type FeatureGridItem,
  type PreFooterCtaEntity,
  type FAQItem,
  type ComparisonCardSectionType,
  type ComponentImage,
  type ComparisonCardItemType,
} from '@lucid/components-react';
import { isDefined } from '@lucid/utils';
import {
  type Drupal_FieldParagraphFeaturedGridSectionFieldFeaturedGridItems,
  type Drupal_ParagraphFeaturedGridItem,
  type Drupal_ParagraphSeoImage,
  type Drupal_FieldParagraphFeatureGridSectionFieldFeatureGridItem,
  type Drupal_ParagraphFeatureGridItem,
  type Drupal_ParagraphPreFooterSection,
  type Drupal_ParagraphCtaButton,
  type Drupal_ParagraphBackNForthSection,
  type Drupal_ParagraphCarouselItem,
  type Drupal_TaxonomyTermAnimations,
  type Drupal_ParagraphFaqItems,
  type Drupal_FieldNodeDiscoveryOrganicFieldDiscoveryFaqItems,
  type Drupal_ParagraphComparisonCard,
} from 'generated/drupal-graphql';

export const mapFaqItems = (item: Drupal_FieldNodeDiscoveryOrganicFieldDiscoveryFaqItems): FAQItem => {
  const entity = item.entity as Drupal_ParagraphFaqItems;
  const entityLinks = entity?.fieldFaqItemLink?.map(link => ({
    linkText: link.title as string,
    linkUrl: link?.url?.path as string,
  }));
  return {
    learnMoreLink: entity?.fieldLearnMoreLink,
    links: entityLinks,
    header: entity?.fieldFaqHeader || '',
    description: entity?.fieldFaqItemDescription,
  };
};

export const mapComparisonCards = (comparisonCards: Drupal_ParagraphComparisonCard[]) =>
  comparisonCards?.map(card => {
    const comparisonCardTitle = card?.fieldComparisonCardTitle;
    const comparisonCardSubtitle = card?.fieldComparisonCardSubtitle;
    const comparisonCardDescription = card?.fieldComparisonCardDescriptio;
    const comparisonSections = card?.fieldComparisonSections?.map(
      section => section.entity,
    ) as ComparisonCardSectionType[];
    const comparisonImage = card?.fieldComparisonCardImage?.entity as Drupal_ParagraphSeoImage;
    const comparisonImageNew = {
      imageAlt: comparisonImage?.fieldAltText,
      imageSrc: comparisonImage?.fieldImageLink,
    } as ComponentImage;
    const comparisonCta = card?.fieldCta;

    return {
      fieldComparisonCardTitle: comparisonCardTitle,
      fieldComparisonCardSubtitle: comparisonCardSubtitle,
      fieldComparisonCardDescription: comparisonCardDescription,
      fieldComparisonSections: comparisonSections,
      fieldCta: comparisonCta,
      fieldImage: comparisonImageNew,
      iconAnimation: 'navigational',
      linkVariant: 'primaryAlternate',
    };
  }) as ComparisonCardItemType[];

export const mapFeaturedGridItems = (
  item: Drupal_FieldParagraphFeaturedGridSectionFieldFeaturedGridItems,
): FeatureGridItem => {
  const entity = item.entity as Drupal_ParagraphFeaturedGridItem;
  const image = entity?.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
  return {
    description: entity?.fieldItemSectionDescription?.processed,
    id: entity?.id,
    title: entity?.fieldTitle,
    image: image && {
      imageAlt: image?.fieldAltText,
      imageSrc: image?.fieldImageLink,
    },
    cta: entity?.fieldCta && {
      linkText: entity?.fieldCta?.title,
      linkUrl: entity?.fieldCta?.url?.path,
    },
  } as FeatureGridItem;
};

export const mapFeatureGridItems = (
  item: Drupal_FieldParagraphFeatureGridSectionFieldFeatureGridItem,
): FeatureGridItem => {
  const entity = item?.entity as Drupal_ParagraphFeatureGridItem;
  const image = entity.fieldFeatureImage?.entity as Drupal_ParagraphSeoImage;
  return {
    description: entity.fieldFeatureDescription,
    id: entity.id,
    title: entity.fieldFeatureTitle,
    image: image && {
      imageAlt: image?.fieldAltText || '',
      imageSrc: image?.fieldImageLink,
    },
  } as FeatureGridItem;
};

export const mapPreFooterCtaData = (preFooter?: Drupal_ParagraphPreFooterSection): PreFooterCtaEntity[] => {
  const preFooterCtas = preFooter?.fieldCtaButton
    ?.filter(isDefined)
    .map(cta => cta.entity as Drupal_ParagraphCtaButton);

  return preFooterCtas?.map(cta => ({
    fieldButtonLink: cta.fieldCtaData?.url?.path || cta.fieldButtonLink,
    fieldButtonText: cta.fieldCtaData?.title || cta.fieldButtonText,
  })) as PreFooterCtaEntity[];
};

export const mapBackNForthItems = (backNForthItems: Drupal_ParagraphBackNForthSection[]) =>
  backNForthItems.reduce<BackNForthSectionProps[]>((previous, current) => {
    const currentSection = current as Drupal_ParagraphBackNForthSection;
    const currentImage = currentSection.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        title: currentSection.fieldTitle as string,
        description: {
          processed: currentSection.fieldSectionDescription?.processed as string,
        },
        cta: {
          linkUrl: currentSection.fieldCta?.url?.path as string,
          linkText: currentSection.fieldCta?.title as string,
        },
        image: {
          imageSrc: currentImage.fieldImageLink as string,
          imageAlt: currentImage.fieldAltText as string,
        },
      },
    ];
  }, []);

export const mapCarouselItems = (carouselItems: Drupal_ParagraphCarouselItem[]) =>
  carouselItems.reduce<CarouselItems[]>((previous, current) => {
    const item = current as Drupal_ParagraphCarouselItem;
    const animationData = item.fieldAnimation?.entity as Drupal_TaxonomyTermAnimations;
    const itemImage = item.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        animation: {
          data: animationData?.fieldAnimationData,
        },
        heading: item.fieldTitle!,
        subheading: item.fieldSubtitle!,
        image: {
          imageAlt: itemImage.fieldAltText! || '',
          imageSrc: itemImage.fieldImageLink!,
        },
        footerText: item.fieldCarouselFooterText!,
      },
    ];
  }, []);
